import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  AlertCircle,
  Copy,
  FileUp,
  PackageCheck,
  RefreshCw,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import {
  localAgentApi,
  type LocalAgentPlatformKey,
  type LocalAgentRelease,
} from '../api/localAgent';
import './LocalAgentDownload.css';

const initialPublishForm = {
  version: '',
  platform: 'windows' as LocalAgentPlatformKey,
  arch: 'x64',
};

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '待发布';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatUpdatedAt(value: string): string {
  if (!value) return '未设置';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function statusLabel(status: string): string {
  if (status === 'published') return '已发布';
  if (status === 'retired') return '已退役';
  return status || '未知';
}

function releaseDownloadURL(release: LocalAgentRelease): string {
  const path = `/api/v1/local-agent/updates/${encodeURIComponent(release.platform)}/${encodeURIComponent(release.version)}/download?arch=${encodeURIComponent(release.arch || 'x64')}`;
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}

function inferVersionFromFileName(fileName: string): string {
  const match = fileName.match(/(?:^|[^0-9])(\d+\.\d+\.\d+)(?:[^0-9]|$)/);
  return match?.[1] ?? '';
}

function inferPlatformFromFileName(fileName: string): LocalAgentPlatformKey | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.dmg')) return 'macos';
  if (lower.endsWith('.exe')) return 'windows';
  return null;
}

export default function LocalAgentReleaseManagement() {
  const [releases, setReleases] = useState<LocalAgentRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [releaseError, setReleaseError] = useState('');
  const [publishForm, setPublishForm] = useState(initialPublishForm);
  const [installerFile, setInstallerFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ loaded: number; total?: number } | null>(null);
  const [retiringId, setRetiringId] = useState('');

  const loadReleases = async () => {
    setLoading(true);
    setReleaseError('');
    try {
      const data = await localAgentApi.listReleases(30);
      setReleases(data.list || []);
    } catch (err) {
      setReleaseError(err instanceof Error ? err.message : '发布记录加载失败');
      setReleases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadReleases();
  }, []);

  const handleCopyReleaseURL = async (release: LocalAgentRelease) => {
    await navigator.clipboard?.writeText(releaseDownloadURL(release));
  };

  const handlePublish = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setReleaseError('');
    if (!installerFile) {
      setReleaseError('请选择 Windows exe 或 macOS dmg 安装包');
      return;
    }
    const lowerFileName = installerFile.name.toLowerCase();
    if (publishForm.platform === 'windows' && !lowerFileName.endsWith('.exe')) {
      setReleaseError('Windows 平台请上传 .exe 安装包');
      return;
    }
    if (publishForm.platform === 'macos' && !lowerFileName.endsWith('.dmg')) {
      setReleaseError('macOS 平台请上传 .dmg 安装包');
      return;
    }
    const installerVersion = inferVersionFromFileName(installerFile.name);
    if (!installerVersion) {
      setReleaseError('安装包文件名必须包含版本号，例如：鸣鹿-0.17.18-win-x64.exe');
      return;
    }
    if (installerVersion !== publishForm.version.trim()) {
      setReleaseError(`安装包版本 ${installerVersion} 与发布版本 ${publishForm.version.trim()} 不一致`);
      return;
    }
    setUploading(true);
    setUploadProgress({ loaded: 0, total: installerFile.size });
    try {
      await localAgentApi.publishRelease({
        version: publishForm.version,
        platform: publishForm.platform,
        arch: publishForm.arch,
        file: installerFile,
        onProgress: (loaded, total) => setUploadProgress({ loaded, total: total ?? installerFile.size }),
      });
      setPublishForm(initialPublishForm);
      setInstallerFile(null);
      await loadReleases();
    } catch (err) {
      setReleaseError(err instanceof Error ? err.message : '发布失败');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleRetire = async (release: LocalAgentRelease) => {
    if (!window.confirm(`确认退役 ${release.platform}/${release.arch} ${release.version}？退役只会将版本从 latest manifest 下架，安装包仍会保留；超过最新 3 个版本后才会自动清理。`)) {
      return;
    }
    setRetiringId(release.id);
    setReleaseError('');
    try {
      await localAgentApi.retireRelease(release.id);
      await loadReleases();
    } catch (err) {
      setReleaseError(err instanceof Error ? err.message : '退役失败');
    } finally {
      setRetiringId('');
    }
  };

  const handleInstallerChange = (file: File | null) => {
    setInstallerFile(file);
    if (!file) return;

    const inferredPlatform = inferPlatformFromFileName(file.name);
    const inferredVersion = inferVersionFromFileName(file.name);
    setPublishForm((prev) => ({
      ...prev,
      platform: inferredPlatform ?? prev.platform,
      version: prev.version || inferredVersion,
    }));
  };

  return (
    <div className="local-agent-page">
      <header className="local-agent-header">
        <div className="local-agent-title-block">
          <div className="local-agent-eyebrow">
            <PackageCheck size={14} />
            本地 Agent 发布管理
          </div>
          <h1 className="local-agent-title">发布管理</h1>
          <p className="local-agent-subtitle">
            上传 Windows/macOS 安装包到广告平台发布源，后端写入 S3、计算 SHA256，并按平台/架构默认保留最新 3 个已发布版本。
          </p>
        </div>
        <div className="local-agent-header-actions">
          <button className="btn btn-outline" type="button" onClick={loadReleases} disabled={loading}>
            <RefreshCw size={14} />
            刷新记录
          </button>
        </div>
      </header>

      <section className="local-agent-card local-agent-section local-agent-release-manager">
        <div className="local-agent-section-head">
          <h2 className="local-agent-section-title">
            <PackageCheck size={15} />
            安装包发布
          </h2>
          <span className="local-agent-section-note">需要发布管理权限；下载页不会展示此操作区</span>
        </div>

        {releaseError && (
          <div className="local-agent-error compact">
            <AlertCircle size={15} />
            {releaseError}
          </div>
        )}

        <form className="local-agent-publish-form" onSubmit={handlePublish}>
          <label className="local-agent-field">
            <span>版本号</span>
            <input
              value={publishForm.version}
              onChange={(event) => setPublishForm((prev) => ({ ...prev, version: event.target.value }))}
              placeholder="0.1.1"
              required
            />
          </label>
          <label className="local-agent-field">
            <span>平台</span>
            <select
              value={publishForm.platform}
              onChange={(event) => setPublishForm((prev) => ({ ...prev, platform: event.target.value as LocalAgentPlatformKey }))}
            >
              <option value="windows">Windows</option>
              <option value="macos">macOS</option>
            </select>
          </label>
          <label className="local-agent-field">
            <span>架构</span>
            <select
              value={publishForm.arch}
              onChange={(event) => setPublishForm((prev) => ({ ...prev, arch: event.target.value }))}
            >
              <option value="x64">x64</option>
            </select>
          </label>
          <label className="local-agent-file-upload">
            <FileUp size={15} />
            <span>{installerFile ? installerFile.name : '选择安装包'}</span>
            <input
              type="file"
              accept=".exe,.dmg"
              onChange={(event) => handleInstallerChange(event.target.files?.[0] ?? null)}
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={uploading}>
            <UploadCloud size={15} />
            {uploading ? '上传中' : '发布安装包'}
          </button>
        </form>
        {uploading && uploadProgress && (
          <div className="local-agent-upload-progress" role="status" aria-live="polite">
            <div className="local-agent-upload-progress-copy">
              <span>正在上传安装包</span>
              <strong>
                {uploadProgress.total
                  ? `${Math.min(100, Math.round((uploadProgress.loaded / uploadProgress.total) * 100))}%`
                  : '上传中'}
              </strong>
            </div>
            <div className="local-agent-upload-progress-track" aria-hidden="true">
              <div
                className="local-agent-upload-progress-value"
                style={{ width: uploadProgress.total ? `${Math.min(100, (uploadProgress.loaded / uploadProgress.total) * 100)}%` : '15%' }}
              />
            </div>
            <span className="local-agent-upload-progress-detail">
              {`${(uploadProgress.loaded / 1024 / 1024).toFixed(1)} MB${uploadProgress.total ? ` / ${(uploadProgress.total / 1024 / 1024).toFixed(1)} MB` : ''}`}
            </span>
          </div>
        )}

        <div className="local-agent-release-table-wrap">
          <table className="local-agent-release-table">
            <thead>
              <tr>
                <th>版本</th>
                <th>平台</th>
                <th>文件</th>
                <th>大小</th>
                <th>SHA256</th>
                <th>状态</th>
                <th>发布时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {releases.length === 0 ? (
                <tr>
                  <td colSpan={8} className="local-agent-empty">
                    暂无发布记录
                  </td>
                </tr>
              ) : (
                releases.map((release) => (
                  <tr key={release.id}>
                    <td>{release.version}</td>
                    <td>{release.platform}/{release.arch}</td>
                    <td className="local-agent-table-file">{release.file_name}</td>
                    <td>{formatBytes(release.size_bytes)}</td>
                    <td className="mono">{release.sha256 || '-'}</td>
                    <td>
                      <span className={`local-agent-badge ${release.status === 'published' ? 'green' : 'amber'}`}>
                        {statusLabel(release.status)}
                      </span>
                    </td>
                    <td>{formatUpdatedAt(release.published_at)}</td>
                    <td>
                      <div className="local-agent-table-actions">
                        <button
                          className="btn btn-sm btn-outline"
                          type="button"
                          onClick={() => handleCopyReleaseURL(release)}
                          disabled={release.status !== 'published'}
                        >
                          <Copy size={13} />
                          复制
                        </button>
                        <button
                          className="btn btn-sm btn-outline danger"
                          type="button"
                          onClick={() => handleRetire(release)}
                          disabled={release.status !== 'published' || retiringId === release.id}
                        >
                          <Trash2 size={13} />
                          退役
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
